import useSWR from 'swr'
import axios from 'axios'
import { API_BASE_URL } from 'config/api'
import { IError, ApiResponse, Meta } from 'utils/fetchers'
import { ProductImage } from 'utils/imageUtils'

const apiUrl = `${API_BASE_URL}/products`

interface Attributes {
  appearance: string
  author: string
  pagesNumber: string
  publishYear: string
  translator: string
}

interface Product {
  attributes: Attributes
  price: number
  title: string
  vendor: string
  barcode: string
  compareAtPrice: number | null
  createdAt: Date
  description: string
  id: string
  images: ProductImage[]
  orderId: string | null
  ownerCellphone: string
  ownerId: string
  ownerLastName: string
}

const fetcher = async (url: string) => {
  try {
    const res = await axios.get(url)
    return res.data
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // Return empty response structure for 404
      return {
        success: true,
        message: 'No results found',
        error: null,
        data: [],
        meta: undefined,
      }
    }
    throw error
  }
}

export function useSearchProduct(
  query: string | undefined,
  categoryId?: string | null,
) {
  // Build URL with query parameters
  let url: string | null = null

  if (query || categoryId) {
    const params = new URLSearchParams()

    if (query) {
      params.append('query', query)
    }

    if (categoryId) {
      params.append('categoryId', categoryId)
    }

    url = `${apiUrl}/search?${params.toString()}`
  }

  const { data: response, error } = useSWR<ApiResponse<Product[]>, IError | null>(
    url,
    fetcher,
  )

  // Extract data and meta from response
  const products = response?.data || undefined
  const meta = response?.meta

  // Check for API-level errors
  const apiError = response && !response.success ? response.error : null
  const finalError = error || apiError

  return {
    products,
    isLoading: !finalError && !response,
    isError: !!finalError,
    error: finalError,
    meta: meta as Meta | undefined,
  }
}
